/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from 'react';
import {
  signIn,
  signOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  updateMFAPreference
} from 'aws-amplify/auth';
import { useAuth } from './authHelpers';

export type MfaTypeV6 = 'SMS' | 'TOTP' | 'EMAIL';

export type SignInStep =
  | 'DONE'
  | 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
  | 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
  | 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
  | 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
  | 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'
  | 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP'
  | 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION'
  | 'RESET_PASSWORD'
  | 'CONFIRM_SIGN_UP';

export interface AuthState {
  availableMfaMethods?: MfaTypeV6[];
  currentFlow: 'login' | 'newPassword' | 'mfa' | 'mfaSelection' | 'mfaSetup' | 'smsSetup' | 'passwordReset';
  loading: boolean;
  errorMessage: string;
  message: string;
  mfaType: MfaTypeV6 | null;
  totpSecret: string;
  totpUri: string;
  phoneNumber?: string;
}

export interface TOTPSetupDetails {
  sharedSecret: string;
  getSetupUri?: (label: string) => string;
}

export const useAuthFlow = (appName: string, onAuthComplete: () => void): any => {
  const { refreshCurrentUser } = useAuth();

  const [state, setState] = useState<AuthState>({
    loading: false,
    errorMessage: '',
    message: '',
    currentFlow: 'login',
    mfaType: null,
    totpSecret: '',
    totpUri: '',
  });

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, loading }));
  }, []);

  const setError = useCallback((errorMessage: string) => {
    setState(prev => ({ ...prev, errorMessage, message: '' }));
  }, []);

  const setMessage = useCallback((message: string) => {
    setState(prev => ({ ...prev, message, errorMessage: '' }));
  }, []);

  const resetState = useCallback(() => {
    setState({
      loading: false,
      errorMessage: '',
      message: '',
      currentFlow: 'login',
      mfaType: null,
      totpSecret: '',
      totpUri: '',
    });
  }, []);

  const handleAuthComplete = useCallback(async () => {
    if (refreshCurrentUser) await refreshCurrentUser();
    await onAuthComplete();

  }, [refreshCurrentUser, onAuthComplete]);

  const handleTotpSetup = useCallback((nextStep: any, email: string) => {
    setMessage('Setting up your authenticator app...');
    let details: TOTPSetupDetails | null = null;

    if ('totpSetupDetails' in nextStep) {
      details = nextStep.totpSetupDetails;
    }

    if (!details) {
      setError('Missing TOTP setup details from sign-in response.');
      return;
    }

    const secret = details.sharedSecret ?? '';
    let uri = '';

    if (typeof details.getSetupUri === 'function') {
      const label = appName + ': ' + email;
      uri = details.getSetupUri(label).toString();
    }

    setState(prev => ({
      ...prev,
      currentFlow: 'mfaSetup',
      totpSecret: secret,
      totpUri: uri,
      message: '',
      errorMessage: ''
    }));
  }, [appName, setMessage, setError]);

  const handleError = async (error: any): Promise<void> => {
    const code = error?.name || error?.code;
    const message = error?.message || '';
    if (code === 'UserNotFoundException') {
      setError('Invalid login credentials.');
    } else if (code === 'NotAuthorizedException' || code === 'InvalidPasswordException') {
      if (message.includes('Password attempts exceeded')) {
        setError('Login attempts exceeded. Please try again later.');
      } else {
        setError('Invalid credentials.');
      }
    } else if (code === 'PasswordResetRequiredException') {
      setError('Password reset required. Click "Forgot my password".');
    } else if (code === 'UserNotConfirmedException') {
      setError('Your account is not confirmed. Check your email for a confirmation link.');
    } else if (code === 'UserAlreadyAuthenticatedException') {
      await signOut();
      setError('An error occurred. Please try signing in again.');
    } else if (error?.name === 'CodeMismatchException') {
      setError('Invalid verification code');
    } else if (error?.name === 'ExpiredCodeException') {
      setError('Code expired. Generate a fresh code and try again.');

    } else {
      setError(`Failed to sign in: ${error?.message || 'Unknown error'}`);
    };
    console.error('Sign-in error:', error);
  };

  const signInUser = useCallback(async (email: string, password: string) => {
    try {
      setError('');
      setMessage('');
      setLoading(true);

      const result = await signIn({ username: email.trim(), password });
      const step = result.nextStep.signInStep as SignInStep;

      switch (step) {
      case 'DONE': {
        await handleAuthComplete();
        break;
      }

      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED': {
        setState(prev => ({
          ...prev,
          currentFlow: 'newPassword',
          message: 'You need to set a new password.'
        }));
        break;
      }

      case 'CONFIRM_SIGN_IN_WITH_SMS_CODE': {
        setState(prev => ({
          ...prev,
          currentFlow: 'mfa',
          mfaType: 'SMS',
          message: ''
        }));
        break;
      }

      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE': {
        setState(prev => ({
          ...prev,
          currentFlow: 'mfa',
          mfaType: 'TOTP',
          message: ''
        }));
        break;
      }

      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
        handleTotpSetup(result.nextStep, email);
        break;
      }

      case 'RESET_PASSWORD': {
        setState(prev => ({
          ...prev,
          currentFlow: 'passwordReset',
          message: 'Password reset required. Check your email for a reset code.'
        }));
        break;
      }

      case 'CONFIRM_SIGN_UP': {
        setError('Your account is not confirmed. Check your email for a confirmation link or code.');
        break;
      }

      default: {
        setError(`Unsupported step: ${step}`);
      }
      }
    } catch (error: any) {
      await handleError(error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setMessage, handleAuthComplete, handleTotpSetup]);

  const confirmNewPassword = useCallback(async (newPassword: string) => {
    if (!newPassword) {
      setError('Please enter a new password.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const resp = await confirmSignIn({ challengeResponse: newPassword });
      const step = resp.nextStep.signInStep as SignInStep;

      if (step === 'DONE') {
        await handleAuthComplete();
        return;
      }

      // Handle follow-up steps
      if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setState(prev => ({
          ...prev,
          currentFlow: 'mfa',
          mfaType: 'TOTP',
          message: ''
        }));
      } else if (step === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        setState(prev => ({
          ...prev,
          currentFlow: 'mfa',
          mfaType: 'SMS',
          message: 'We sent you a code by SMS. Enter it below.'
        }));
      } else if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        handleTotpSetup(resp.nextStep, ''); // Email not available here
      } else {
        setError(`Unsupported step after password change: ${step}`);
      }
    } catch (error: any) {
      await handleError(error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setMessage, handleAuthComplete, handleTotpSetup]);

  const confirmMfaCode = useCallback(async (code: string) => {
    const cleanCode = (code || '').replace(/\s+/g, '');
    if (!/^\d{6}$/.test(cleanCode)) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const resp = await confirmSignIn({ challengeResponse: cleanCode });
      const step = resp.nextStep.signInStep as SignInStep;

      if (step === 'DONE') {
        await handleAuthComplete();
        return;
      }

      if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        handleTotpSetup(resp.nextStep, '');
        return;
      }
    } catch (error: any) {
      await handleError(error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setMessage, handleAuthComplete, handleTotpSetup]);

  const verifyTotpSetup = useCallback(async (code: string) => {
    if (!code) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    try {
      setError('');
      setMessage('');
      setLoading(true);

      const { nextStep } = await confirmSignIn({ challengeResponse: code });

      if (nextStep.signInStep === 'DONE') {
        try {
          await updateMFAPreference({ totp: 'PREFERRED' });
        } catch {
          // preference is optional; ignore failures
        }
        setMessage('Signed in successfully.');
        await handleAuthComplete();
        return;
      }
    } catch (error: any) {
      await handleError(error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setMessage, handleAuthComplete]);

  const requestPasswordReset = useCallback(async (email: string) => {
    if (!email) {
      setError('Please enter your email address to reset your password');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('Sending reset code... Please wait.');

    try {
      await resetPassword({ username: email.trim() });
      setState(prev => ({
        ...prev,
        currentFlow: 'passwordReset',
        message: ''
      }));
    } catch (error: any) {
      setMessage('');
      await handleError(error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setMessage]);

  const confirmPasswordReset = useCallback(async (email: string, confirmationCode: string, newPassword: string) => {
    if (!confirmationCode || !newPassword) {
      setError('Please enter both confirmation code and new password');
      return;
    }

    setLoading(true);

    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode,
        newPassword,
      });

      setState(prev => ({
        ...prev,
        currentFlow: 'login',
        message: 'Password has been reset successfully. You can now log in.'
      }));
    } catch (error: any) {
      await handleError(error);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError]);

  return {
    state,
    actions: {
      signInUser,
      confirmNewPassword,
      confirmMfaCode,
      verifyTotpSetup,
      requestPasswordReset,
      confirmPasswordReset,
      resetState,
      setError,
      setMessage
    }
  };
};
